import React, { useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ExternalLink, Mail, HardDrive, BookOpen, Ticket, Calendar } from 'lucide-react';
import { DataRecord, DataDomain, DomainKey, timeAgo, isStale } from './types';

// Row-detail slide-over panel (open decision #3 — detail panel + link-out).
// Slides in from the right so the table stays visible behind it; Escape,
// backdrop click, or the X closes it. "View in <source>" links out.

const DOMAIN_ICONS: Record<DomainKey, React.FC<{
  size?: number;
  className?: string;
}>> = {
  email: Mail,
  drive: HardDrive,
  confluence: BookOpen,
  jira: Ticket,
  calendar: Calendar
};
const SOURCE_LABELS: Record<DomainKey, string> = {
  email: 'Gmail',
  drive: 'Google Drive',
  confluence: 'Confluence',
  jira: 'Jira',
  calendar: 'Google Calendar'
};
interface Props {
  record: DataRecord | null;
  domain: DataDomain;
  onClose: () => void;
}
export function RecordPanel({
  record,
  domain,
  onClose
}: Props) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);
  const Icon = DOMAIN_ICONS[domain.key];
  const sourceLabel = SOURCE_LABELS[domain.key];
  return <AnimatePresence data-id="element-746">
      {record && <motion.div key="record-panel-backdrop" className="fixed inset-0 z-50 bg-black/50" initial={{
      opacity: 0
    }} animate={{
      opacity: 1
    }} exit={{
      opacity: 0
    }} onClick={onClose} data-id="element-747">
          <motion.aside className="absolute right-0 top-0 bottom-0 w-full max-w-[520px] bg-[#16161b] border-l border-white/[0.06] flex flex-col" initial={{
        x: '100%'
      }} animate={{
        x: 0
      }} exit={{
        x: '100%'
      }} transition={{
        type: 'spring',
        stiffness: 340,
        damping: 36
      }} onClick={e => e.stopPropagation()} data-id="element-748">
            {/* Header */}
            <div className="flex items-start justify-between gap-4 px-6 pt-6 pb-4 border-b border-white/[0.06]" data-id="element-749">
              <div className="flex items-start gap-3 min-w-0" data-id="element-750">
                <div className="w-10 h-10 rounded-[12px] flex items-center justify-center shrink-0" style={{
              background: `${domain.color}18`
            }} data-id="element-751">
                  <Icon size={18} style={{
                color: domain.color
              }} data-id="element-752" />
                </div>
                <div className="min-w-0" data-id="element-753">
                  <h2 className="text-[16px] font-medium text-[#e8e4df] leading-snug" data-id="element-754">{record.title}</h2>
                  <div className="flex items-center gap-2 mt-1.5 flex-wrap" data-id="element-755">
                    <span className="text-[11px] px-2 py-0.5 rounded-full font-medium" style={{
                  background: `${domain.color}20`,
                  color: domain.color
                }} data-id="element-756">
                      {domain.label}
                    </span>
                    <span className={`text-[11px] ${isStale(record.syncedAt) ? 'text-[#e8a84c]' : 'text-[#8a8680]'}`} data-id="element-757">
                      synced {timeAgo(record.syncedAt)}
                      {isStale(record.syncedAt) && ' (stale)'}
                    </span>
                  </div>
                </div>
              </div>
              <button onClick={onClose} className="w-8 h-8 flex items-center justify-center hover:bg-white/5 rounded-lg shrink-0" data-id="element-758">
                <X size={18} className="text-[#8a8680]" data-id="element-759" />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto px-6 py-5" data-id="element-760">
              {/* Fields */}
              <div className="bg-[#1c1c22] rounded-[16px] px-5 py-2" data-id="element-761">
                {record.detail.map((field, i) => <div key={field.label} className={`flex items-start justify-between gap-4 py-3 ${i !== record.detail.length - 1 ? 'border-b border-white/[0.04]' : ''}`} data-id="element-762">
                    <span className="text-[13px] text-[#8a8680] shrink-0 w-[100px]" data-id="element-763">{field.label}</span>
                    <span className="text-[13px] text-[#e8e4df] text-right break-words min-w-0" data-id="element-764">
                      {field.value}
                    </span>
                  </div>)}
              </div>

              {/* Content preview */}
              {record.body && <div className="mt-4" data-id="element-765">
                  <div className="text-[11px] text-[#8a8680]/60 uppercase tracking-wider mb-2" data-id="element-766">Content</div>
                  <div className="bg-[#1c1c22] rounded-[16px] p-5" data-id="element-767">
                    <pre className="whitespace-pre-wrap text-[13px] text-[#c4bfb8] leading-relaxed font-sans" data-id="element-768">
                      {record.body}
                    </pre>
                  </div>
                </div>}
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t border-white/[0.06]" data-id="element-769">
              {record.externalUrl ? <a href={record.externalUrl} target="_blank" rel="noopener noreferrer" className="flex items-center justify-center gap-2 w-full py-2.5 rounded-[12px] bg-[#252530] text-[#e8e4df] text-[13px] font-medium hover:bg-[#2a2a35] transition-colors" data-id="element-770">
                  <ExternalLink size={14} data-id="element-771" />
                  View in {sourceLabel}
                </a> : <div className="text-center text-[12px] text-[#8a8680]/50 py-2" data-id="element-772">
                  No source link available for this record
                </div>}
            </div>
          </motion.aside>
        </motion.div>}
    </AnimatePresence>;
}